import { useState } from "react";
import { toast } from "sonner";
import { Download, Loader2, Receipt } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Empty, PageState } from "@/components/app/Common";
import { api, blobUrl, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/ui";
import { useLoad } from "@/hooks/useLoad";

const money = (p) => `$${p.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${p.currency}`;

const ReceiptButton = ({ p }) => {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const a = document.createElement("a");
      a.href = await blobUrl(`/billing/payments/${p.provider}/${p.id}/receipt`);
      a.download = `AccruallQ-receipt-${p.reference}.pdf`;
      a.click();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };
  return (
    <Button variant="ghost" size="sm" onClick={download} disabled={busy} data-testid={`receipt-download-${p.id}`}>
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}<span className="ml-1.5">Receipt</span>
    </Button>
  );
};

export const PaymentHistory = () => {
  const state = useLoad(() => api.get("/billing/payments").then((r) => r.data));
  const rows = state.data?.payments || [];
  return (
    <div className="surface p-6 mt-10" data-testid="payment-history">
      <div className="font-semibold">Payment history</div>
      <div className="text-sm text-muted-foreground mb-4">Every card and PayPal payment for this workspace, with a downloadable receipt.</div>
      <PageState state={state}>
        {state.data?.partial && <p className="text-xs text-amber-600 dark:text-amber-400 mb-3" data-testid="payment-history-partial">Some payments couldn't be loaded from the payment provider right now. Refresh to try again.</p>}
        {!rows.length ? <Empty icon={Receipt} title="No payments yet" text="Your receipts will appear here after your first subscription payment." testId="payment-history-empty" /> : (
          <div className="divide-y divide-border">
            {rows.map((p) => (
              <div key={`${p.provider}-${p.id}`} className="flex flex-wrap items-center gap-x-4 gap-y-1 py-3 text-sm" data-testid="payment-row">
                <span className="w-28 text-muted-foreground">{fmtDate(p.date)}</span>
                <span className="flex-1 min-w-[180px] font-medium">{p.description}</span>
                <span className={`text-xs rounded-full px-2 py-0.5 ${p.provider === "paypal" ? "bg-[#FFC439]/20 text-[#8a6100] dark:text-[#FFC439]" : "bg-muted text-muted-foreground"}`}>{p.method}</span>
                <span className={`text-xs ${p.status === "Paid" ? "text-emerald-600 dark:text-emerald-400" : "text-amber-600"}`}>{p.status}</span>
                <span className="font-mono w-28 text-right">{money(p)}</span>
                <ReceiptButton p={p} />
              </div>
            ))}
          </div>
        )}
      </PageState>
    </div>
  );
};
